import React, { useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import ReactMapBoxGl, { Popup, Marker, GeoJSONLayer } from 'react-mapbox-gl';

import { COUNTRIES, resolveCountryFromBirthText, resolveCountryByLabel } from './countryData';
import { getPrismicStructuredTextPlain, getPrismicSizedImageUrl } from '../../utils/functions';
import IndividualMap from './individualMap';
import './style.scss';

const Map = ReactMapBoxGl({
  accessToken: process.env.GATSBY_MAPBOX_API_KEY,
  scrollZoom: false,
  minZoom: 1.3,
  maxZoom: 1.3,
});

const MARKER_DIAMETER_PX = 40;
/** Space between portrait edges in a cluster */
const MARKER_GAP_PX = 10;
const SLOTS_PER_RING = 6;

/** Outer ring at 10, 12, 2, 4, 6, 8 o'clock (screen y-down, 0 rad = east) */
const SPIDER_CLOCK_ANGLES = [
  -Math.PI / 2 - Math.PI / 3,
  -Math.PI / 2,
  -Math.PI / 2 + Math.PI / 3,
  -Math.PI / 2 + (2 * Math.PI) / 3,
  -Math.PI / 2 + Math.PI,
  -Math.PI / 2 + (4 * Math.PI) / 3,
];

const SPIDER_RING_RADIUS =
  Math.ceil((MARKER_DIAMETER_PX + MARKER_GAP_PX) / (2 * Math.sin(Math.PI / SLOTS_PER_RING))) + 2;

/** England / Wales / UK share one map pin — spider layout only here */
const UK_CLUSTER_TALLY_KEY = COUNTRIES.england.coordinates.join(',');

const getClusterLayout = (tallyKey) => (tallyKey === UK_CLUSTER_TALLY_KEY ? 'spider' : 'ring');

const getMemberMapPlace = (elementData, showBy) => {
  if (showBy === 'birthCountry') {
    const birthText = getPrismicStructuredTextPlain(elementData?.birthCountry);
    return (
      resolveCountryFromBirthText(birthText) ||
      resolveCountryByLabel(birthText) ||
      resolveCountryByLabel(elementData?.birthCountry?.[0]?.text)
    );
  }

  return (
    resolveCountryByLabel(elementData?.[showBy]?.[0]?.text) ||
    COUNTRIES[elementData?.[showBy]?.[0]?.text?.toLowerCase()]
  );
};

const getMemberMapTallyKey = (elementData, showBy) => {
  if (showBy === 'birthCountry') {
    const birthText = getPrismicStructuredTextPlain(elementData?.birthCountry) || '';
    const country =
      resolveCountryFromBirthText(birthText) || resolveCountryByLabel(birthText);
    if (country?.coordinates) return country.coordinates.join(',');
    return birthText.toLowerCase().replace(/\bphill?ippines\b/gi, 'philippines') || 'unknown';
  }

  const label = elementData?.[showBy]?.[0]?.text;
  const country = resolveCountryByLabel(label);
  if (country?.coordinates) return country.coordinates.join(',');
  return String(label || 'unknown').toLowerCase();
};

const ringRadiusFor = (ring) => {
  const minRingRadius = Math.ceil(
    (MARKER_DIAMETER_PX + MARKER_GAP_PX) / (2 * Math.sin(Math.PI / SLOTS_PER_RING))
  );
  return minRingRadius + (ring - 1) * (MARKER_DIAMETER_PX + MARKER_GAP_PX);
};

/** UK: hub + clock ring; other clusters (e.g. Philippines): full ring, no center hub */
const getClusterPixelOffset = (clusterIndex, clusterSize, layout) => {
  if (clusterSize <= 1) return [0, 0];

  if (layout === 'spider') {
    if (clusterIndex === 0) return [0, 0];

    const ringSlotIndex = clusterIndex - 1;
    const ring = Math.floor(ringSlotIndex / SLOTS_PER_RING) + 1;
    const posInRing = ringSlotIndex % SLOTS_PER_RING;
    const angle = SPIDER_CLOCK_ANGLES[posInRing];
    const radius = SPIDER_RING_RADIUS + (ring - 1) * (MARKER_DIAMETER_PX + MARKER_GAP_PX);

    return [Math.round(Math.cos(angle) * radius), Math.round(Math.sin(angle) * radius)];
  }

  const ring = Math.floor(clusterIndex / SLOTS_PER_RING) + 1;
  const posInRing = clusterIndex % SLOTS_PER_RING;
  const angle = (posInRing / SLOTS_PER_RING) * 2 * Math.PI - Math.PI / 2;
  const radius = ringRadiusFor(ring);

  return [Math.round(Math.cos(angle) * radius), Math.round(Math.sin(angle) * radius)];
};

const buildMarkerPlacements = (mappingList, showByKey) => {
  const clusterSizes = {};
  (mappingList ?? []).forEach((member) => {
    const key = getMemberMapTallyKey(member.data, showByKey);
    clusterSizes[key] = (clusterSizes[key] || 0) + 1;
  });

  const clusterSeen = {};
  return (mappingList ?? []).map((member, index) => {
    const tallyKey = getMemberMapTallyKey(member.data, showByKey);
    const clusterIndex = clusterSeen[tallyKey] ?? 0;
    clusterSeen[tallyKey] = clusterIndex + 1;
    const clusterSize = clusterSizes[tallyKey] || 1;
    const clusterLayout = getClusterLayout(tallyKey);

    return {
      member,
      index,
      place: getMemberMapPlace(member.data, showByKey),
      pixelOffset: getClusterPixelOffset(clusterIndex, clusterSize, clusterLayout),
      clusterSize,
      clusterLayout,
      memberKey: `${member.data?.name?.[0]?.text ?? 'member'}-${showByKey}-${index}`,
    };
  });
};

const HOVER_CLEAR_DELAY_MS = 240;

const getMemberActionPhotoSrc = (member) =>
  getPrismicSizedImageUrl(member?.data?.actionPhoto?.url, 120) ||
  getPrismicSizedImageUrl(member?.data?.portraitPhoto?.url, 120);

const MapMarker = ({ placement, onPress, onHoverEnter, onHoverLeave, previewActive }) => {
  const { member, place, pixelOffset, clusterSize, clusterLayout, memberKey } = placement;
  const elementData = member?.data;
  const coordinates = place?.coordinates;
  const portraitSrc = getPrismicSizedImageUrl(elementData?.portraitPhoto?.url, 120);
  const actionSrc = getMemberActionPhotoSrc(member);

  if (!coordinates?.length) {
    return null;
  }

  return (
    <Marker coordinates={coordinates} offset={pixelOffset} anchor="center" key={memberKey}>
      <div
        className={`map-marker-hit${clusterSize > 1 ? ' map-marker-hit--clustered' : ''}${
          previewActive ? ' map-marker-hit--preview' : ''
        }`}
        onMouseEnter={() => {
          onHoverEnter({
            polygon: place?.tileset,
            popup: {
              coordinate: coordinates,
              member,
              offset: pixelOffset,
            },
          });
        }}
        onMouseLeave={onHoverLeave}
        onClick={onPress}
      >
        <div
          className={`map-marker${clusterSize > 1 ? ' map-marker--clustered' : ''}${
            clusterLayout === 'spider' &&
            clusterSize > 1 &&
            pixelOffset[0] === 0 &&
            pixelOffset[1] === 0
              ? ' map-marker--cluster-hub'
              : ''
          }`}
        >
          <img className="map-marker__photo map-marker__photo--portrait" src={portraitSrc} alt="" />
          {actionSrc && actionSrc !== portraitSrc && (
            <img className="map-marker__photo map-marker__photo--action" src={actionSrc} alt="" />
          )}
        </div>
      </div>
    </Marker>
  );
};

const MapComponent = ({ mappingList = [], handleSelect }) => {
  const [popup, setPopup] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [showBy, setShowBy] = useState({
    key: 'currentCountry',
    title: 'CURRENTLY BASED',
  });
  const markerPlacements = useMemo(
    () => buildMarkerPlacements(mappingList, showBy.key),
    [mappingList, showBy.key]
  );
  const hoverActiveRef = useRef(false);
  const clearHoverTimerRef = useRef(null);

  const cancelHoverClear = () => {
    if (clearHoverTimerRef.current) {
      clearTimeout(clearHoverTimerRef.current);
      clearHoverTimerRef.current = null;
    }
  };

  const handleMarkerHoverEnter = ({ popup: nextPopup, polygon }) => {
    cancelHoverClear();
    hoverActiveRef.current = true;
    setPopup(nextPopup);
    if (polygon) {
      setHovered({ polygon });
    }
  };

  const handleMarkerHoverLeave = () => {
    hoverActiveRef.current = false;
    cancelHoverClear();
    clearHoverTimerRef.current = setTimeout(() => {
      if (hoverActiveRef.current) return;
      setHovered(null);
      setPopup(null);
    }, HOVER_CLEAR_DELAY_MS);
  };

  return (
    <>
      <link href="https://api.mapbox.com/mapbox-gl-js/v1.10.1/mapbox-gl.css" rel="stylesheet" />
      <Map
        style="mapbox://styles/lineagejourney/ckhz34cwa0xux1alun7bzqmq7"
        containerStyle={{
          height: '100%',
          width: '100%',
        }}
        center={[20.743871, 30.329916]}
        zoom={[1.3]}
        onClick={() => {
          setPopup(null);
        }}
      >
        <div className="map-overlay">
          <div className="switch">
            <span className={`current ${isEqual(showBy.key, 'currentCountry') ? 'active' : ''}`}>CURRENTLY BASED</span>
            <label className="btn-switch">
              <input
                type="checkbox"
                onChange={() => {
                  cancelHoverClear();
                  hoverActiveRef.current = false;
                  setHovered(null);
                  setPopup(null);
                  if (isEqual(showBy.key, 'currentCountry')) {
                    setShowBy({ key: 'birthCountry', title: '' });
                  } else {
                    setShowBy({
                      key: 'currentCountry',
                      title: 'CURRENTLY BASED',
                    });
                  }
                }}
              />
              <span className="switch-slider"></span>
            </label>
            <span className={`family ${isEqual(showBy.key, 'birthCountry') ? 'active' : ''}`}>BIRTH COUNTRY</span>
          </div>
        </div>
        {markerPlacements.map((placement) => (
          <MapMarker
            key={placement.memberKey}
            placement={placement}
            onPress={() => handleSelect(placement.index)}
            onHoverEnter={handleMarkerHoverEnter}
            onHoverLeave={handleMarkerHoverLeave}
            previewActive={popup?.member?.id === placement.member?.id}
          />
        ))}
        {popup && (
          <Popup
            anchor="bottom"
            offset={[popup.offset?.[0] ?? 0, (popup.offset?.[1] ?? 0) - 28]}
            key={popup.member.id}
            coordinates={popup.coordinate}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: 120,
                pointerEvents: 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    height: 35,
                    width: 35,
                    borderRadius: 60,
                    borderWidth: 1,
                    borderColor: 'white',
                    borderStyle: 'solid',
                  }}
                >
                  <img src={getMemberActionPhotoSrc(popup.member)} alt="" height="40" width="40" />
                </div>
              </div>
              <div style={{ flex: 2, padding: 5 }}>
                <p
                  style={{
                    fontSize: 9,
                    fontFamily: 'Lato',
                    color: '#0A0A0A',
                    textAlign: 'center',
                    fontWeight: '700',
                    lineHeight: '12px',
                  }}
                >
                  {popup?.member?.data?.name[0]?.text.toUpperCase()}
                </p>
              </div>
            </div>
          </Popup>
        )}
        {hovered?.polygon && (
          <GeoJSONLayer
            data={hovered.polygon}
            fillPaint={{
              'fill-color': '#FFDB4D',
              'fill-opacity': 0.6,
            }}
          />
        )}
      </Map>
    </>
  );
};

export { IndividualMap };
export default MapComponent;
